import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdsReportRow, DemandBucket, DemandDate, DemandInsights, DemandRow, HoursMonth, Report, ReportKind, SoldReport as SoldReportData, SoldRow } from '@smile/shared';
import { api, ApiError } from '../api/client';
import { CZK, TYPE_LABEL, TYPE_SHORT, zaznamWord } from '../lib/format';
import { pageList } from '../lib/filters';

/**
 * Sestavy — the three legacy reports, drawn as the legacy drew them: a
 * plain table with the legacy's column names (Partner · Rezervací · Tržba ·
 * Pr. cena · Pr. dní) and a Celkem footer. They sit on the Statistiky
 * screen as tabs so the toolbar above them is the one the staff already
 * know; nothing on the menu changes.
 */
const KC2 = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function errorCard(error: unknown, what: string, refetch: () => void) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="card state" role="alert">
        Sestavy jsou manažerská obrazovka — na tenhle účet nejsou zpřístupněné.
      </div>
    );
  }
  return (
    <div className="card state" role="alert">
      {what} se nepodařilo načíst.
      {error instanceof Error && error.message ? ` (${error.message})` : ''}{' '}
      <button className="btn sm" type="button" onClick={refetch}>
        Zkusit znovu
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Partneři · Promokódy
 * ------------------------------------------------------------------ */

export function GroupReport({ kind, params }: { kind: ReportKind; params: Record<string, unknown> }) {
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['report', kind, params],
    queryFn: () => api.report(kind, params),
    placeholderData: keepPreviousData,
  });
  const partners = kind === 'partners';
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      {isPending ? (
        <div className="state" role="status" aria-busy="true">
          Načítám…
        </div>
      ) : isError ? (
        errorCard(error, partners ? 'Partnery' : 'Promokódy', refetch)
      ) : (
        <div className="tablewrap sestavawrap">
          <table className="sestava">
            <thead>
              <tr>
                <th>{partners ? 'Partner' : 'Promokód'}</th>
                {!partners && <th>Ceník</th>}
                <th className="num">Rezervací</th>
                <th className="num">Tržba</th>
                <th className="num">Pr. cena</th>
                <th className="num">Pr. dní</th>
              </tr>
            </thead>
            <tbody>
              {(data as Report).rows.length === 0 && (
                <tr>
                  <td colSpan={partners ? 5 : 6} className="state">
                    Žádná data pro tento výběr.
                  </td>
                </tr>
              )}
              {(data as Report).rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <b>
                      {partners ? (
                        <Link to={`/rezervace?partnerId=${encodeURIComponent(r.key)}`}>{r.label}</Link>
                      ) : (
                        <Link to={`/rezervace?q=${encodeURIComponent(r.label)}`}>{r.label}</Link>
                      )}
                    </b>
                  </td>
                  {!partners && <td className="was">{r.detail ?? ''}</td>}
                  <td className="num">{CZK.format(r.count)}</td>
                  <td className="num">{CZK.format(r.revenue)} Kč</td>
                  <td className="num">{KC2.format(r.avgPrice)} Kč</td>
                  <td className="num">{r.avgDays == null ? '—' : KC2.format(r.avgDays)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="pagetotal">
                <td colSpan={partners ? 1 : 2}>
                  Celkem: {CZK.format((data as Report).total.groups)}
                  {isFetching ? ' · aktualizuji' : ''}
                </td>
                <td className="num">{CZK.format((data as Report).total.count)}</td>
                <td className="num">{CZK.format((data as Report).total.revenue)} Kč</td>
                <td className="num">{KC2.format((data as Report).total.avgPrice)} Kč</td>
                <td className="num">{(data as Report).total.avgDays == null ? '—' : KC2.format((data as Report).total.avgDays!)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div className="basis" style={{ padding: '8px 14px' }}>
        Tržba je cena parkování + služby + penále za pozdní odjezd, jako v původní sestavě; Statistiky nahoře počítají jen
        parkování. {partners ? 'Jen rezervace založené partnerským účtem.' : 'Jen rezervace s promokódem.'}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Prodané služby a upgrady
 * ------------------------------------------------------------------ */

function saleText(r: SoldRow): string {
  if (r.item === 'extraservice') return r.newValue;
  return `${TYPE_SHORT[r.oldValue] ?? r.oldValue} → ${TYPE_SHORT[r.newValue] ?? r.newValue}`;
}

export function SoldReport({
  from,
  to,
  q,
  page,
  onPage,
  canTick,
}: {
  from: string;
  to: string;
  q: string;
  page: number;
  onPage: (p: number) => void;
  canTick: boolean;
}) {
  const qc = useQueryClient();
  const params = useMemo(() => ({ from, to, q: q || undefined, page }), [from, to, q, page]);
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['sold', params],
    queryFn: () => api.sold(params),
    placeholderData: keepPreviousData,
  });
  const tick = useMutation({
    mutationFn: (v: { row: SoldRow; on: boolean }) =>
      api.setCommission({
        changedStored: v.row.changedStored,
        reservationId: v.row.reservationId,
        item: v.row.item,
        externalId: v.row.externalId,
        countCommission: v.on,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sold'] }),
  });
  if (isPending) {
    return (
      <div className="card state" role="status" aria-busy="true" style={{ marginTop: 14 }}>
        Načítám…
      </div>
    );
  }
  if (isError) return <div style={{ marginTop: 14 }}>{errorCard(error, 'Prodané služby', refetch)}</div>;
  const d = data as SoldReportData;
  const totalPages = Math.max(1, Math.ceil(d.total / d.perPage));
  const tickErr = tick.error instanceof ApiError ? tick.error.serverMessage ?? tick.error.message : tick.error instanceof Error ? tick.error.message : null;

  return (
    <>
      <div className="card tablecard" style={{ marginTop: 14 }}>
        <div className="tablewrap sestavawrap">
          <table className="sestava sold">
            <thead>
              <tr>
                <th>Dispečer</th>
                <th className="num">ID rezervace</th>
                <th>Klient</th>
                <th>Služba / upgrade</th>
                <th className="num">Cena</th>
                <th title="Započítat cenu do součtu provizí pro dispečery?">Provize?</th>
                <th className="num">Datum prodeje</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="state">
                    Žádný prodej v tomto období.
                  </td>
                </tr>
              )}
              {d.rows.map((r) => {
                const key = `${r.changedStored}|${r.reservationId}|${r.item}|${r.externalId}`;
                return (
                  <tr key={key}>
                    <td>
                      <b>{r.userName}</b>
                    </td>
                    <td className="num">
                      <Link to={`/rezervace/${r.reservationId}`}>{r.reservationId}</Link>
                    </td>
                    <td>
                      {r.driverName}
                      {r.plate ? <span className="was"> · {r.plate}</span> : null}
                    </td>
                    <td>
                      <small className="was">{r.item === 'type' ? 'upgrade' : r.oldValue === 'removed' ? 'odebráno' : 'služba'}</small> {saleText(r)}
                    </td>
                    <td className="num">
                      <b className={r.amount < 0 ? 'neg' : r.amount > 0 ? 'pos' : undefined}>{CZK.format(r.amount)} Kč</b>
                    </td>
                    <td>
                      <label className="chk">
                        <input
                          type="checkbox"
                          checked={r.countCommission}
                          disabled={!canTick || tick.isPending}
                          onChange={(e) => tick.mutate({ row: r, on: e.target.checked })}
                          aria-label={`Započítat prodej #${r.reservationId} do provize`}
                        />
                        počítat
                      </label>
                    </td>
                    <td className="num">
                      {r.changed.slice(8, 10).replace(/^0/, '')}. {r.changed.slice(5, 7).replace(/^0/, '')}. {r.changed.slice(0, 4)}{' '}
                      <small className="was">{r.changed.slice(11, 16)}</small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pager">
          <span>
            Strana {d.page} z {CZK.format(totalPages)} · {CZK.format(d.total)} {zaznamWord(d.total)}
            {isFetching ? ' · aktualizuji' : ''}
          </span>
          {tickErr && (
            <span className="arrive-error" role="alert">
              {tickErr}
            </span>
          )}
          <span className="pages">
            {pageList(d.page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`e${i}`} className="gap">
                  …
                </span>
              ) : (
                <button key={p} type="button" className={p === d.page ? 'pg on' : 'pg'} onClick={() => onPage(p)} aria-current={p === d.page ? 'page' : undefined}>
                  {p}
                </button>
              ),
            )}
          </span>
        </div>
      </div>

      <div className="card tablecard" style={{ marginTop: 14 }}>
        <h2 style={{ padding: '12px 14px 0' }}>Provize dispečerů</h2>
        <div className="tablewrap sestavawrap">
          <table className="sestava standing">
            <thead>
              <tr>
                <th>Dispečer</th>
                <th className="num">Upgrady</th>
                {d.services.map((s) => (
                  <th key={s.id} className="num" title={s.title}>
                    {s.title.length > 20 ? `${s.title.slice(0, 20)}…` : s.title}
                  </th>
                ))}
                <th className="num">Celkem</th>
                <th className="num">Provize 10 %</th>
              </tr>
            </thead>
            <tbody>
              {d.standing.length === 0 && (
                <tr>
                  <td colSpan={d.services.length + 4} className="state">
                    Nikdo v tomto období nic neprodal.
                  </td>
                </tr>
              )}
              {d.standing.map((s) => (
                <tr key={s.userId}>
                  <td>
                    <b>{s.userName}</b>
                  </td>
                  <td className="num">{s.columns.type ? `${CZK.format(s.columns.type)} Kč` : ''}</td>
                  {d.services.map((svc) => (
                    <td key={svc.id} className="num">
                      {s.columns[String(svc.id)] ? `${CZK.format(s.columns[String(svc.id)]!)} Kč` : ''}
                    </td>
                  ))}
                  <td className="num">
                    <b>{CZK.format(s.total)} Kč</b>
                  </td>
                  <td className="num">
                    <em>{CZK.format(s.commission)} Kč</em>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="basis" style={{ padding: '8px 14px' }}>
          Součet za celé období a hledání, ne jen za stránku; jen prodeje označené „počítat“. Provize je 10 % z každého sloupce,
          zaokrouhleno, jako v původní sestavě. {CZK.format(d.total)} {zaznamWord(d.total)} celkem.
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Reklama — Google Ads next to the direct bookings of the month
 * (docs/GOOGLE-ADS.md). Not a legacy report: the owner asked for it on
 * 28 October 2026 to see what a direct booking costs in advertising
 * against the partners' commission.
 * ------------------------------------------------------------------ */

const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
function monthLabel(m: string): string {
  const [y, mm] = m.split('-');
  return `${MONTH_CS[Number(mm) - 1] ?? mm} ${y}`;
}
const PCT1 = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function AdsCells({ r }: { r: AdsReportRow }) {
  return (
    <>
      <td className="num">{CZK.format(r.cost)} Kč</td>
      <td className="num">{CZK.format(r.clicks)}</td>
      <td className="num">{CZK.format(r.conversions)}</td>
      <td className="num">{CZK.format(r.bookings)}</td>
      <td className="num">{CZK.format(r.newBookings)}</td>
      <td className="num">{r.adBookings ? CZK.format(r.adBookings) : ''}</td>
      <td className="num">{CZK.format(r.revenue)} Kč</td>
      <td className="num">{r.costPerBooking === null ? '' : `${CZK.format(r.costPerBooking)} Kč`}</td>
      <td className="num">{r.costPerNewBooking === null ? '' : `${CZK.format(r.costPerNewBooking)} Kč`}</td>
      <td className="num">{r.costPerAdBooking === null ? '' : `${CZK.format(r.costPerAdBooking)} Kč`}</td>
      <td className="num">{r.costShare === null ? '' : `${PCT1.format(r.costShare)} %`}</td>
      <td className="num">{r.revenuePerCrown === null ? '' : `${PCT1.format(r.revenuePerCrown)} Kč`}</td>
    </>
  );
}

export function AdsReport({ from, to }: { from: string; to: string }) {
  const params = useMemo(() => ({ from, to }), [from, to]);
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['ads', params],
    queryFn: () => api.ads(params),
    placeholderData: keepPreviousData,
  });
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 60_000 });
  if (!from || !to) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Zadejte období od–do nahoře; reklama se počítá po měsících.
      </div>
    );
  }
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      {isPending ? (
        <div className="state" role="status" aria-busy="true">
          Načítám…
        </div>
      ) : isError ? (
        errorCard(error, 'Reklamu', refetch)
      ) : (
        <>
          <div className="tablewrap sestavawrap">
            <table className="sestava">
              <thead>
                <tr>
                  <th>Měsíc</th>
                  <th className="num">Google Ads</th>
                  <th className="num">Prokliky</th>
                  <th className="num">Konverze (Google)</th>
                  <th className="num">Přímé rezervace</th>
                  <th className="num">z toho noví</th>
                  <th className="num">z reklamy</th>
                  <th className="num">Tržba přímých</th>
                  <th className="num">Reklama na rezervaci</th>
                  <th className="num">Reklama na nového</th>
                  <th className="num">Reklama na rezervaci z reklamy</th>
                  <th className="num">Podíl z tržby</th>
                  <th className="num">Tržba na 1 Kč reklamy</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={13} className="state">
                      {health.data && health.data.googleAds === false
                        ? 'Napojení na Google Ads není v tomto nasazení zapnuté.'
                        : 'Žádná data pro tento výběr.'}
                    </td>
                  </tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.month}>
                    <td>
                      <b>{monthLabel(r.month)}</b>
                    </td>
                    <AdsCells r={r} />
                  </tr>
                ))}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot>
                  <tr className="pagetotal">
                    <td>
                      <b>Celkem</b>
                    </td>
                    <AdsCells r={data.total} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="basis" style={{ padding: '8px 14px' }}>
            Google Ads účet {data.customerId.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
            {data.fetchedAt ? `, staženo ${new Date(data.fetchedAt).toLocaleString('cs-CZ')}` : ', zatím nestaženo'}
            {data.firstDate ? `, data od ${data.firstDate}` : ''}. Přímé rezervace = zdroj Web (bez partnerů, ParkVia a
            dovolenaparking), podle data vytvoření, bez smazaných a nedojezdů; tržba = cena parkování. „Noví“ = přímé bez
            klubového účtu — vracející se člen reklamu nepotřeboval, takže reklama na rezervaci je spodní odhad a reklama na
            nového horní. „Z reklamy“ = rezervace s kódem kliknutí z Google (web ho ukládá od 28. 10. 2026) — přesné číslo,
            od toho data. Konverze jsou Googlem počítané, ne naše rezervace. Posledních 35 dní se stahuje znovu — Google čísla ještě doplňuje.
            {isFetching ? ' Aktualizuji…' : ''}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Poptávka — the quote log: what was asked for against what was booked
 * (docs/PRICING-INSTRUMENTATION.md). The pricing review of 28 October
 * 2026 asked for it before any dynamic structure: lost demand was invisible.
 * ------------------------------------------------------------------ */

const TYPE_CS: Record<string, string> = { cheap: 'Venkovní', regular: 'Garážové', exclusive: 'VIP' };
const CHANNEL_CS: Record<string, string> = { desk: 'dispečink', partner: 'partner (API)', sandbox: 'nastavení', website: 'web' };

function DemandTableView({ from, to }: { from: string; to: string }) {
  const params = useMemo(() => ({ from, to }), [from, to]);
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['demand', params],
    queryFn: () => api.demand(params),
    placeholderData: keepPreviousData,
  });
  if (!from || !to) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Zadejte období od–do nahoře (podle dne příjezdu).
      </div>
    );
  }
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      {isPending ? (
        <div className="state" role="status" aria-busy="true">
          Načítám…
        </div>
      ) : isError ? (
        errorCard(error, 'Poptávku', refetch)
      ) : (
        <>
          <div className="tablewrap sestavawrap">
            <table className="sestava">
              <thead>
                <tr>
                  <th>Měsíc příjezdu</th>
                  <th className="num">Poptávek</th>
                  <th className="num">Odmítnuto (není místo)</th>
                  <th className="num">Rezervací</th>
                  <th className="num">Tržba rezervací</th>
                  <th className="num">Rezervací z poptávek</th>
                </tr>
              </thead>
              <tbody>
                {data.months.length === 0 && (
                  <tr>
                    <td colSpan={6} className="state">
                      {data.enabled ? 'Žádné poptávky v tomto období (log běží od nasazení).' : 'Log poptávek není v tomto nasazení zapnutý.'}
                    </td>
                  </tr>
                )}
                {data.months.map((m) => (
                  <tr key={m.month}>
                    <td>
                      <b>{monthLabel(m.month)}</b>
                    </td>
                    <td className="num">{CZK.format(m.quotes)}</td>
                    <td className="num">{m.refused ? CZK.format(m.refused) : ''}</td>
                    <td className="num">{CZK.format(m.bookings)}</td>
                    <td className="num">{CZK.format(m.bookedPrice)} Kč</td>
                    <td className="num">{m.quotes ? `${PCT1.format((100 * m.bookings) / m.quotes)} %` : ''}</td>
                  </tr>
                ))}
              </tbody>
              {data.months.length > 0 && (
                <tfoot>
                  <tr className="pagetotal">
                    <td>
                      <b>Celkem</b>
                    </td>
                    <td className="num">{CZK.format(data.total.quotes)}</td>
                    <td className="num">{data.total.refused ? CZK.format(data.total.refused) : ''}</td>
                    <td className="num">{CZK.format(data.total.bookings)}</td>
                    <td className="num">{CZK.format(data.total.bookedPrice)} Kč</td>
                    <td className="num">{data.total.quotes ? `${PCT1.format((100 * data.total.bookings) / data.total.quotes)} %` : ''}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {data.rows.length > 0 && (
            <div className="tablewrap sestavawrap" style={{ marginTop: 8 }}>
              <table className="sestava mini">
                <thead>
                  <tr>
                    <th>Den příjezdu</th>
                    <th>Produkt</th>
                    <th>Kanál</th>
                    <th className="num">Poptávek</th>
                    <th className="num">Odmítnuto</th>
                    <th className="num">Pr. nabídnutá cena</th>
                    <th className="num">Rezervací</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.slice(0, 400).map((r: DemandRow) => (
                    <tr key={`${r.day}-${r.type}-${r.channel}`}>
                      <td>{r.day}</td>
                      <td>{TYPE_CS[r.type] ?? r.type}</td>
                      <td>{CHANNEL_CS[r.channel] ?? r.channel}</td>
                      <td className="num">{CZK.format(r.quotes)}</td>
                      <td className="num">{r.refused ? CZK.format(r.refused) : ''}</td>
                      <td className="num">{r.avgPrice === null ? '' : `${CZK.format(r.avgPrice)} Kč`}</td>
                      <td className="num">{r.bookings ? CZK.format(r.bookings) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="basis" style={{ padding: '8px 14px' }}>
            Poptávka = každé ocenění: formulář dispečinku, partnerské API, nastavení a web smileparking.cz (každá cena,
            kterou web zobrazí) — jedna poptávka na kanál, den příjezdu, den odjezdu, produkt a hodinu. Odmítnuto = bez
            ceny, bez místa nebo zavřeno. Rezervace = ty, které vytvořil nový systém nebo web; rezervace ze starého
            extranetu tu nejsou. Podle dne příjezdu. {data.since ? `Od ${data.since}.` : ''}
            {isFetching ? ' Aktualizuji…' : ''}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Poptávka: the table by arrival day (as before), then "Hledání v období" —
 * the searches made in the chosen days, cut by stay length, lead time, hour,
 * weekday, product, the most asked-for dates and the price per arrival date
 * (the owner, 29 October 2026).
 */
export function DemandReportView({ from, to }: { from: string; to: string }) {
  return (
    <>
      <DemandTableView from={from} to={to} />
      {from && to ? <DemandInsightsView from={from} to={to} /> : null}
    </>
  );
}

const WEEKDAY_CS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const CHANNEL_PICK: Array<[string, string]> = [['all', 'Vše'], ['website', 'Web'], ['desk', 'Dispečink'], ['partner', 'Partneři']];
const PRODUCT_PICK: Array<[string, string]> = [['all', 'Vše'], ['cheap', 'Venkovní'], ['regular', 'Garážové'], ['exclusive', 'VIP']];
const kc = (v: number | null) => (v === null ? '' : `${CZK.format(v)} Kč`);
const czDate = (d: string) => `${Number(d.slice(8, 10))}. ${Number(d.slice(5, 7))}.`;
const czWeekday = (d: string) => WEEKDAY_CS[(new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7];

/** Columns in the dashboard's chart style (.chartcard/.bars/.col). */
function ColumnChart({ title, cap, cols, unit }: { title: string; cap?: string; cols: Array<{ key: string; label: string; value: number; tip: string }>; unit?: string }) {
  const max = Math.max(1, ...cols.map((c) => c.value));
  const step = Math.max(1, Math.ceil(max / 3));
  const top = step * 3;
  const dense = cols.length > 40;
  const every = dense ? Math.ceil(cols.length / 16) : cols.length > 24 ? 2 : 1;
  return (
    <div className="card chartcard statseries" style={{ marginTop: 14 }}>
      <h4>{title}</h4>
      {cap ? <div className="basis">{cap}</div> : null}
      {cols.length === 0 ? (
        <div className="state">Žádná data.</div>
      ) : (
        <>
          <div className={dense ? 'bars dense' : 'bars'}>
            {[1, 2, 3].map((n) => (
              <div key={n} className="gridline" style={{ bottom: `${n * 33}%` }}>
                <span>
                  {CZK.format(step * n)}
                  {unit ? ` ${unit}` : ''}
                </span>
              </div>
            ))}
            {cols.map((c) => (
              <div key={c.key} className="col" data-tip={c.tip}>
                <div className="stack">
                  {c.value > 0 ? <div className="seg" style={{ height: `${(c.value / top) * 150}px`, background: 'var(--c1)' }} /> : null}
                </div>
              </div>
            ))}
          </div>
          <div className="baseline" />
          <div className={dense ? 'xlab dense' : 'xlab'}>
            {cols.map((c, i) => (
              <span key={c.key}>{i % every === 0 ? c.label : ''}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** A breakdown as the stats screen draws it (.statcard/.hbars): name, bar, searches, and the numbers beside. */
function BucketCard({ title, cap, rows }: { title: string; cap: string; rows: Array<DemandBucket & { name: string }> }) {
  const max = Math.max(1, ...rows.map((r) => r.searches));
  const sum = rows.reduce((s, r) => s + r.searches, 0);
  return (
    <div className="card statcard">
      <h4>{title}</h4>
      <div className="cap">{cap}</div>
      {sum === 0 ? (
        <div className="state">Žádná data.</div>
      ) : (
        <>
          <div className="hbars" role="list">
            {rows.map((r) => (
              <div
                className="r"
                key={r.name}
                role="listitem"
                aria-label={`${r.name}: ${r.searches} hledání, ${PCT1.format((100 * r.searches) / sum)} %`}
                title={`${r.name}: odmítnuto ${r.refused}, pr. cena ${kc(r.avgPrice)}, pr. za den ${kc(r.avgPerDay)}, rezervací ${r.bookings}`}
              >
                <div className="n" aria-hidden="true">{r.name}</div>
                <div className="t" aria-hidden="true">
                  <i style={{ width: `${(r.searches / max) * 100}%` }} />
                </div>
                <div className="v" aria-hidden="true">{CZK.format(r.searches)}</div>
                <div className="pc" aria-hidden="true">{PCT1.format((100 * r.searches) / sum)} %</div>
              </div>
            ))}
          </div>
          <table className="sestava mini" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th />
                <th className="num">Odmítnuto</th>
                <th className="num">Pr. cena</th>
                <th className="num">Pr. za den</th>
                <th className="num">Rezervací</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .filter((r) => r.searches || r.bookings)
                .map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    <td className="num">{r.refused ? CZK.format(r.refused) : ''}</td>
                    <td className="num">{kc(r.avgPrice)}</td>
                    <td className="num">{kc(r.avgPerDay)}</td>
                    <td className="num">{r.bookings ? CZK.format(r.bookings) : ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function DemandInsightsView({ from, to }: { from: string; to: string }) {
  const [channel, setChannel] = useState('all');
  const [product, setProduct] = useState('all');
  const params = useMemo(() => ({ from, to, channel }), [from, to, channel]);
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['demand-insights', params],
    queryFn: () => api.demandInsights(params),
    placeholderData: keepPreviousData,
  });
  const head = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 22 }}>
      <h3 style={{ margin: 0, fontSize: 15 }}>Hledání v období</h3>
      <span className="basis" style={{ margin: 0 }}>
        podle okamžiku hledání (ne příjezdu), {czDate(from)} – {czDate(to)}
      </span>
      <div className="tabs" role="group" aria-label="Kanál" style={{ marginLeft: 'auto' }}>
        {CHANNEL_PICK.map(([k, label]) => (
          <button key={k} type="button" className={channel === k ? 'active' : ''} onClick={() => setChannel(k)}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
  if (isPending) return <>{head}<div className="card state" role="status" aria-busy="true" style={{ marginTop: 10 }}>Načítám…</div></>;
  if (isError) return <>{head}<div className="card" style={{ marginTop: 10 }}>{errorCard(error, 'Hledání', refetch)}</div></>;
  const d: DemandInsights = data;
  if (!d.enabled) return <>{head}<div className="card state" style={{ marginTop: 10 }}>Log poptávek není v tomto nasazení zapnutý.</div></>;
  const perDayOf = (x: DemandDate) => (product === 'all' ? x.avgPerDay : (x.perDay[product] ?? null));
  return (
    <>
      {head}
      <div className="kpis" style={{ marginTop: 10 }}>
        <div className="card kpi">
          <div className="l">Hledání</div>
          <div className="v">{CZK.format(d.searches)}</div>
          <div className="d">odmítnuto {CZK.format(d.refused)}{d.searches ? ` (${PCT1.format((100 * d.refused) / d.searches)} %)` : ''}</div>
        </div>
        <div className="card kpi">
          <div className="l">Rezervací</div>
          <div className="v">{CZK.format(d.bookings)}</div>
          <div className="d">{d.searches ? `${PCT1.format((100 * d.bookings) / d.searches)} % z hledání` : ''}</div>
        </div>
        <div className="card kpi">
          <div className="l">Pr. nabídnutá cena</div>
          <div className="v">{kc(d.avgPrice) || '—'}</div>
          <div className="d">{d.avgPerDay !== null ? `${CZK.format(d.avgPerDay)} Kč za den` : ''}</div>
        </div>
        <div className="card kpi">
          <div className="l">Pr. délka · předstih</div>
          <div className="v">{d.avgDays !== null ? `${PCT1.format(d.avgDays)} dne` : '—'}</div>
          <div className="d">{d.avgLead !== null ? `hledají ${CZK.format(d.avgLead)} dní před příjezdem` : ''}</div>
        </div>
      </div>

      <ColumnChart
        title="Kdy lidé hledají — hodina dne"
        cap="Počet hledání podle hodiny, kdy proběhlo (pražský čas). Najeďte myší na sloupec."
        cols={d.byHour.map((h) => ({ key: String(h.hour), label: `${h.hour}`, value: h.searches, tip: `${h.hour}:00–${h.hour}:59 — hledání ${h.searches}, rezervací ${h.bookings}` }))}
      />

      <div className="statgrid" style={{ marginTop: 14 }}>
        <BucketCard title="Na kolik dní" cap="Délka pobytu, na kterou se hledalo" rows={d.byLength.map((b) => ({ ...b, name: b.label }))} />
        <BucketCard title="Jak dlouho předem" cap="Dní mezi hledáním a příjezdem" rows={d.byLead.map((b) => ({ ...b, name: b.label }))} />
        <BucketCard title="Produkt" cap="Na který produkt se hledalo" rows={d.byProduct.map((p) => ({ ...p, name: TYPE_CS[p.type] ?? p.type }))} />
        <BucketCard
          title="Den v týdnu"
          cap="Kdy v týdnu lidé hledají"
          rows={d.byWeekday.map((w) => ({ name: WEEKDAY_CS[w.weekday - 1] ?? String(w.weekday), label: '', searches: w.searches, refused: 0, avgPrice: null, avgPerDay: null, bookings: w.bookings }))}
        />
      </div>

      <h3 style={{ margin: '22px 0 0', fontSize: 14 }}>Návštěvy webu</h3>
      {d.visits === null ? (
        <div className="card state" style={{ marginTop: 10 }}>
          Návštěvy, zařízení, jazyk, příchod z reklamy a zadané promokódy se začnou zapisovat po nahrání nové verze
          webu (smile-quote-log.php a index.php).{d.bots ? ` Hledání robotů v období: ${CZK.format(d.bots)} (nejsou v číslech výše).` : ''}
        </div>
      ) : (
        <>
          <div className="kpis" style={{ marginTop: 10 }}>
            <div className="card kpi">
              <div className="l">Návštěv s hledáním</div>
              <div className="v">{CZK.format(d.visits)}</div>
              <div className="d">{d.visits ? `${PCT1.format(d.searches / d.visits)} hledání na návštěvu` : ''}</div>
            </div>
            <div className="card kpi">
              <div className="l">Návštěv s rezervací</div>
              <div className="v">{CZK.format(d.visitsBooked ?? 0)}</div>
              <div className="d">{d.visits ? `${PCT1.format((100 * (d.visitsBooked ?? 0)) / d.visits)} % návštěv` : ''}</div>
            </div>
            <div className="card kpi">
              <div className="l">Roboti</div>
              <div className="v">{CZK.format(d.bots)}</div>
              <div className="d">hledání vyhledávačů a skriptů — v číslech nejsou</div>
            </div>
          </div>
          <div className="statgrid" style={{ marginTop: 14 }}>
            <BucketCard title="Zařízení" cap="Mobil, počítač, tablet" rows={d.byDevice.map((b) => ({ ...b, name: b.label }))} />
            <BucketCard title="Odkud přišli" cap="Kliknutím na reklamu Google, nebo jinak" rows={d.bySource.map((b) => ({ ...b, name: b.label }))} />
            <BucketCard title="Jazyk webu" cap="V jakém jazyce si web prohlíželi" rows={d.byLanguage.map((b) => ({ ...b, name: b.label }))} />
          </div>
          {d.byRefusal.length > 0 && (
            <div className="card tablecard" style={{ marginTop: 14 }}>
              <div style={{ padding: '12px 14px 0' }}>
                <h4 style={{ margin: 0 }}>Proč web nenabídl cenu</h4>
                <div className="basis">Odmítnutá hledání podle první kontroly, která cenu zastavila (web posílá od 4. 11. 2026).</div>
              </div>
              <div className="tablewrap sestavawrap">
                <table className="sestava mini">
                  <thead>
                    <tr>
                      <th>Důvod</th>
                      <th className="num">Hledání</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.byRefusal.map((r) => (
                      <tr key={r.reason}>
                        <td>{r.label}</td>
                        <td className="num">{CZK.format(r.searches)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {d.promos.length > 0 && (
            <div className="card tablecard" style={{ marginTop: 14 }}>
              <div style={{ padding: '12px 14px 0' }}>
                <h4 style={{ margin: 0 }}>Zadané promokódy</h4>
                <div className="basis">Kódy, které lidé sami napsali (zimní „zima“, který web dosazuje sám, tu není).</div>
              </div>
              <div className="tablewrap sestavawrap">
                <table className="sestava mini">
                  <thead>
                    <tr>
                      <th>Kód</th>
                      <th className="num">Hledání</th>
                      <th className="num">Návštěv, které pak rezervovaly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.promos.map((p) => (
                      <tr key={p.code}>
                        <td>{p.code}</td>
                        <td className="num">{CZK.format(p.searches)}</td>
                        <td className="num">{p.bookings ? CZK.format(p.bookings) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div className="card tablecard" style={{ marginTop: 14 }}>
        <div style={{ padding: '12px 14px 0' }}>
          <h4 style={{ margin: 0 }}>Nejžádanější termíny příjezdu</h4>
        </div>
        <div className="tablewrap sestavawrap">
          <table className="sestava mini">
            <thead>
              <tr>
                <th>Příjezd</th>
                <th className="num">Hledání</th>
                <th className="num">Odmítnuto</th>
                <th className="num">Obvyklá délka</th>
                <th className="num">Pr. cena</th>
                <th className="num">Pr. za den</th>
                <th className="num">Venkovní / den</th>
                <th className="num">Garáž / den</th>
                <th className="num">VIP / den</th>
                <th className="num">Rezervací</th>
              </tr>
            </thead>
            <tbody>
              {d.topDates.length === 0 && (
                <tr>
                  <td colSpan={10} className="state">Žádná hledání v tomto období.</td>
                </tr>
              )}
              {d.topDates.map((x) => (
                <tr key={x.date}>
                  <td>
                    {czWeekday(x.date)} {czDate(x.date)} {x.date.slice(0, 4)}
                  </td>
                  <td className="num">{CZK.format(x.searches)}</td>
                  <td className="num">{x.refused ? CZK.format(x.refused) : ''}</td>
                  <td className="num">{x.medianDays !== null ? `${x.medianDays} d` : ''}</td>
                  <td className="num">{kc(x.avgPrice)}</td>
                  <td className="num">{kc(x.avgPerDay)}</td>
                  <td className="num">{kc(x.perDay.cheap ?? null)}</td>
                  <td className="num">{kc(x.perDay.regular ?? null)}</td>
                  <td className="num">{kc(x.perDay.exclusive ?? null)}</td>
                  <td className="num">{x.bookings ? CZK.format(x.bookings) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14 }}>
        <div className="tabs" role="group" aria-label="Produkt">
          {PRODUCT_PICK.map(([k, label]) => (
            <button key={k} type="button" className={product === k ? 'active' : ''} onClick={() => setProduct(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <ColumnChart
        title="Průměrná nabídnutá cena za den podle data příjezdu"
        cap="Každý sloupec je den příjezdu, na který se v tomto období hledalo; výška = průměrná cena za den pobytu, kterou systém nabídl."
        unit="Kč"
        cols={d.byArrival.map((x) => {
          const v = perDayOf(x);
          return { key: x.date, label: czDate(x.date), value: v ?? 0, tip: `${czWeekday(x.date)} ${czDate(x.date)} — ${v === null ? 'bez ceny' : `${CZK.format(v)} Kč/den`}, hledání ${x.searches}${x.refused ? `, odmítnuto ${x.refused}` : ''}` };
        })}
      />
      <ColumnChart
        title="Kolik hledání na který den příjezdu"
        cols={d.byArrival.map((x) => ({ key: x.date, label: czDate(x.date), value: x.searches, tip: `${czWeekday(x.date)} ${czDate(x.date)} — hledání ${x.searches}, odmítnuto ${x.refused}, rezervací ${x.bookings}` }))}
      />
      <div className="basis" style={{ padding: '8px 2px' }}>
        Hledání = jedno ocenění na kanál, den příjezdu, den odjezdu, produkt a hodinu (web se ptá na cenu při každé změně
        formuláře). Bez ceny / bez místa / zavřeno = odmítnuto. Ceny jsou ty, které systém nabídl, ne zaplacené. Rezervace
        = vytvořené novým systémem nebo webem v tomto období.{isFetching ? ' Aktualizuji…' : ''}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Hodiny — paid hours by role and person, from shifts (dispatch) and
 * transfer stamps (drivers, first → last transfer of the day — the owner's
 * definition). Instrument 2 of docs/PRICING-INSTRUMENTATION.md.
 * ------------------------------------------------------------------ */

const ROLE_CS: Record<string, string> = { driver: 'řidič', dispatch: 'dispečink' };

export function HoursReportView({ from, to }: { from: string; to: string }) {
  const [rate, setRate] = useState(270);
  const [open, setOpen] = useState<string | null>(null);
  const params = useMemo(() => ({ from, to, rate }), [from, to, rate]);
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['hours', params],
    queryFn: () => api.hours(params),
    placeholderData: keepPreviousData,
    enabled: !!from && !!to,
  });
  if (!from || !to) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Zadejte období od–do nahoře.
      </div>
    );
  }
  const months = data?.months ?? [];
  const sum = (k: keyof HoursMonth) => months.reduce((s, m) => s + (typeof m[k] === 'number' ? (m[k] as number) : 0), 0);
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      <div style={{ padding: '12px 14px 0', display: 'flex', gap: 12, alignItems: 'center' }}>
        <label>
          Sazba vč. odvodů (Kč/h){' '}
          <input type="number" min={0} max={5000} step={10} value={rate} onChange={(e) => setRate(Number(e.target.value) || 0)} style={{ width: 80 }} />
        </label>
        {isFetching && <span className="empty">Aktualizuji…</span>}
      </div>
      {isPending ? (
        <div className="state" role="status" aria-busy="true">
          Načítám…
        </div>
      ) : isError ? (
        errorCard(error, 'Hodiny', refetch)
      ) : (
        <>
          <div className="tablewrap sestavawrap">
            <table className="sestava">
              <thead>
                <tr>
                  <th>Měsíc</th>
                  <th className="num">Řidiči</th>
                  <th className="num">Hodiny řidičů</th>
                  <th className="num">Dny</th>
                  <th className="num">Převozů</th>
                  <th className="num">Převozů / h</th>
                  <th className="num">Dispečeři</th>
                  <th className="num">Hodiny dispečinku</th>
                  <th className="num">Směn</th>
                  <th className="num">Náklad</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {months.length === 0 && (
                  <tr>
                    <td colSpan={11} className="state">
                      Žádné směny ani převozy v tomto období.
                    </td>
                  </tr>
                )}
                {months.map((m) => (
                  <tr key={m.month}>
                    <td>
                      <b>{monthLabel(m.month)}</b>
                      {m.partialFrom || m.partialTo ? (
                        <span className="empty">
                          {' '}
                          jen {m.partialFrom ? `od ${Number(m.partialFrom.slice(8, 10))}. ${Number(m.partialFrom.slice(5, 7))}.` : ''}
                          {m.partialTo ? ` do ${Number(m.partialTo.slice(8, 10))}. ${Number(m.partialTo.slice(5, 7))}.` : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{m.drivers}</td>
                    <td className="num">{CZK.format(m.driverHours)}</td>
                    <td className="num">{CZK.format(m.driverDays)}</td>
                    <td className="num">{CZK.format(m.transfers)}</td>
                    <td className="num">{m.transfersPerHour ?? ''}</td>
                    <td className="num">{m.dispatchers}</td>
                    <td className="num">{CZK.format(m.dispatchHours)}</td>
                    <td className="num">{m.dispatchShifts}</td>
                    <td className="num">
                      {CZK.format(m.cost)} Kč{m.salariedHours ? <span className="empty"> (+{CZK.format(m.salariedHours)} h fixní)</span> : null}
                    </td>
                    <td>
                      <button type="button" className="btn sm" onClick={() => setOpen(open === m.month ? null : m.month)}>
                        {open === m.month ? 'skrýt' : 'lidé'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {months.length > 1 && (
                <tfoot>
                  <tr className="pagetotal">
                    <td>
                      <b>Celkem</b>
                    </td>
                    <td />
                    <td className="num">{CZK.format(sum('driverHours'))}</td>
                    <td className="num">{CZK.format(sum('driverDays'))}</td>
                    <td className="num">{CZK.format(sum('transfers'))}</td>
                    <td className="num">{sum('driverHours') ? (sum('transfers') / sum('driverHours')).toFixed(1) : ''}</td>
                    <td />
                    <td className="num">{CZK.format(sum('dispatchHours'))}</td>
                    <td className="num">{sum('dispatchShifts')}</td>
                    <td className="num">{CZK.format(sum('cost'))} Kč</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {open &&
            months
              .filter((m) => m.month === open)
              .map((m) => (
                <div key={m.month} className="tablewrap sestavawrap" style={{ marginTop: 8 }}>
                  <table className="sestava mini">
                    <thead>
                      <tr>
                        <th>{monthLabel(m.month)}</th>
                        <th>Role</th>
                        <th className="num">Hodiny</th>
                        <th className="num">Dny / směny</th>
                        <th className="num">Převozů</th>
                        <th className="num">Převozů / h</th>
                        <th className="num">Náklad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.people.map((p) => (
                        <tr key={`${p.role}-${p.userId}`}>
                          <td>{p.name}</td>
                          <td>{ROLE_CS[p.role] ?? p.role}{p.salaried ? ' · fixní' : ''}</td>
                          <td className="num">{CZK.format(p.hours)}</td>
                          <td className="num">{p.days}</td>
                          <td className="num">{p.role === 'driver' ? CZK.format(p.transfers) : ''}</td>
                          <td className="num">{p.transfersPerHour ?? ''}</td>
                          <td className="num">{p.salaried ? <span className="empty">fixní</span> : `${CZK.format(p.cost)} Kč`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
          <div className="basis" style={{ padding: '8px 14px' }}>
            Řidič: den od první do poslední zapsané přepravy (Europort ↔ parkoviště), hodiny = rozdíl; práce bez zápisu
            přepravy (mytí, dvůr) tu není. Dispečink: uzavřené směny (Směny), začátek → konec; směna delší než 24 h se
            nepočítá. Náklad = hodiny × sazba nahoře. Vedoucí (účty admin — Aziz, Anushervon) mají hodiny vidět, ale platí se
            fixně, do nákladu nejdou. Fakturující řidiči jsou v hodinách, jejich faktury tu nejsou. Neúplný měsíc má u názvu
            „jen od/do“.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Statistiky → Starý systém (the owner, 30 Oct 2026): who still works in the old extranet and on
 * which pages, from its usage log. Account names are shown here only (admins); never copied anywhere.
 */
export function UsageReportView({ from, to }: { from: string; to: string }) {
  const params = useMemo(() => ({ from, to }), [from, to]);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['usage', params],
    queryFn: () => api.usage(params),
    placeholderData: keepPreviousData,
    enabled: !!from && !!to,
  });
  if (!from || !to) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Zadejte období od–do nahoře.
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="card state" role="status" aria-busy="true" style={{ marginTop: 14 }}>
        Načítám…
      </div>
    );
  }
  if (isError) return <div style={{ marginTop: 14 }}>{errorCard(error, 'Starý systém', refetch)}</div>;
  if (!data.available) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Záznam používání starého systému se nepodařilo přečíst{data.error ? ` (${data.error})` : ''}.
      </div>
    );
  }
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      <div style={{ padding: '12px 14px 0' }}>
        <b>{CZK.format(data.total)}</b> požadavků ve starém systému · {data.accounts.length} účtů
      </div>
      <div className="tablewrap sestavawrap">
        <table className="sestava">
          <thead>
            <tr>
              <th>Účet</th>
              <th>Role</th>
              <th className="num">Požadavků</th>
              <th className="num">Dnů</th>
              <th>Naposledy</th>
              <th>Nejčastější stránky</th>
              <th>Nejčastější akce</th>
            </tr>
          </thead>
          <tbody>
            {data.accounts.map((a) => (
              <tr key={a.userId}>
                <td>{a.name}</td>
                <td>{a.role}</td>
                <td className="num">{CZK.format(a.requests)}</td>
                <td className="num">{a.days}</td>
                <td>{a.lastAt.slice(8, 10)}. {a.lastAt.slice(5, 7)}. {a.lastAt.slice(11, 16)}</td>
                <td>{a.pages.slice(0, 5).map((p) => `${p.page} ×${p.count}`).join(', ')}</td>
                <td>{a.actions.slice(0, 5).map((x) => `${x.page}: ${x.keys || '—'} ×${x.count}`).join('; ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="tablewrap sestavawrap" style={{ marginTop: 14 }}>
        <table className="sestava">
          <thead>
            <tr>
              <th>Stránka</th>
              <th className="num">Požadavků</th>
              <th className="num">Účtů</th>
            </tr>
          </thead>
          <tbody>
            {data.pages.map((p) => (
              <tr key={p.page}>
                <td>{p.page}</td>
                <td className="num">{CZK.format(p.count)}</td>
                <td className="num">{p.accounts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* What people press: the page and the names of the fields/buttons sent — no values are logged. */}
      <div className="tablewrap sestavawrap" style={{ marginTop: 14 }}>
        <table className="sestava">
          <thead>
            <tr>
              <th>Akce (stránka)</th>
              <th>Odeslaná pole a tlačítka</th>
              <th className="num">Kolikrát</th>
              <th className="num">Účtů</th>
            </tr>
          </thead>
          <tbody>
            {data.actions.length === 0 && (
              <tr>
                <td colSpan={4} className="empty">Žádné odeslané formuláře v tomto období.</td>
              </tr>
            )}
            {data.actions.slice(0, 100).map((x) => (
              <tr key={`${x.page}|${x.keys}`}>
                <td>{x.page}</td>
                <td style={{ wordBreak: 'break-word' }}>{x.keys || '—'}</td>
                <td className="num">{CZK.format(x.count)}</td>
                <td className="num">{x.accounts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Statistiky → Auto místa (the owner, 30 Oct 2026): per arrival day, who gave the cars their
 * Doporučené místo — the system, the system then a dispatcher, only a dispatcher, nobody — and how
 * often the system's zone was where the car was really parked.
 */
export function AutoTargetReportView({ from, to }: { from: string; to: string }) {
  const params = useMemo(() => ({ from, to }), [from, to]);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['autotarget-report', params],
    queryFn: () => api.autotarget(params),
    placeholderData: keepPreviousData,
    enabled: !!from && !!to,
  });
  if (!from || !to) {
    return (
      <div className="card state" role="status" style={{ marginTop: 14 }}>
        Zadejte období od–do nahoře.
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="card state" role="status" aria-busy="true" style={{ marginTop: 14 }}>
        Načítám…
      </div>
    );
  }
  if (isError) return <div style={{ marginTop: 14 }}>{errorCard(error, 'Auto místa', refetch)}</div>;
  const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)} %` : '—');
  const rows = [...data.days, { ...data.total, date: 'Celkem' }];
  return (
    <div className="card tablecard" style={{ marginTop: 14 }}>
      <div style={{ padding: '12px 14px 0' }}>
        Doporučené místo podle dne příjezdu. Systém vybírá místo 2 h před příjezdem (od 30. 10. 2026 16:57); ruční změna má vždy přednost.
      </div>
      <div className="tablewrap sestavawrap">
        <table className="sestava">
          <thead>
            <tr>
              <th>Den příjezdu</th>
              <th className="num">Příjezdů</th>
              <th className="num">Systém</th>
              <th className="num">Systém → změnil dispečer</th>
              <th className="num">Jen dispečer</th>
              <th className="num">Bez místa</th>
              <th className="num">Podíl systému</th>
              <th className="num">Zaparkováno ve stejné zóně</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.date} className={d.date === 'Celkem' ? 'pagetotal' : undefined}>
                <td>{d.date === 'Celkem' ? <b>Celkem</b> : `${d.date.slice(8, 10)}. ${d.date.slice(5, 7)}. ${d.date.slice(0, 4)}`}</td>
                <td className="num">{CZK.format(d.arrivals)}</td>
                <td className="num">{CZK.format(d.system)}</td>
                <td className="num">{CZK.format(d.changed)}</td>
                <td className="num">{CZK.format(d.person)}</td>
                <td className="num">{CZK.format(d.none)}</td>
                <td className="num">{pct(d.system, d.arrivals)}</td>
                <td className="num" title="Z aut s místem od systému, která už jsou zaparkovaná na Europortu">
                  {d.systemParked ? `${d.systemSameZone} / ${d.systemParked} (${pct(d.systemSameZone, d.systemParked)})` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
